(function () {
  const formatSpecLabel = (key) => String(key).replace(/_/g, ' ').trim();
  const normalizeSpecKey = (key) => String(key).toUpperCase().replace(/_/g, ' ').trim();

  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .replace(/&nbsp;/gi, ' ')
      .replace(/&mdash;/gi, '—')
      .replace(/&ndash;/gi, '–')
      .replace(/&minus;/gi, '-')
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
  };

  const extractRichText = (node) => {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (node.type === 'text' && node.value) return node.value;
    if (!Array.isArray(node.children)) return '';
    return node.children.map(extractRichText).join('');
  };

  const normalizeSpecText = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') {
      if (value.type === 'root') return normalizeSpecText(extractRichText(value));
      return '';
    }
    let text = decodeHtmlEntities(String(value).replace(/<[^>]*>/g, ''));
    text = text.replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, '').trim();
    text = text.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D–—―−－]/g, '-');
    return text;
  };

  const isPlaceholderValue = (text) => {
    if (!text) return true;
    if (/^-+$/.test(text)) return true;
    return ['n/a', 'na', 'none', 'null', 'undefined', 'tbd'].includes(text.toLowerCase());
  };

  const flattenSpecValues = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) return value.flatMap(flattenSpecValues);
    if (typeof value === 'object') return [value];
    return [value];
  };

  const isEmptySpecValue = (value) => {
    const values = flattenSpecValues(value);
    if (values.length === 0) return true;
    return values.every((v) => isPlaceholderValue(normalizeSpecText(v)));
  };

  const isLabelValueSpecs = (specs) =>
    Array.isArray(specs) &&
    specs.length > 0 &&
    specs.every((item) => item && typeof item === 'object' && 'label' in item);

  const getSpecKeysFromProduct = (specs) => {
    if (!specs) return [];
    if (Array.isArray(specs)) {
      if (isLabelValueSpecs(specs)) return specs.map((item) => item.label);
      return specs.map(([key]) => key);
    }
    return Object.keys(specs);
  };

  const getSpecValues = (product, key) => {
    const specs = product?.specs;
    if (!specs) return null;
    if (Array.isArray(specs)) {
      if (isLabelValueSpecs(specs)) {
        const item = specs.find((s) => normalizeSpecKey(s.label) === normalizeSpecKey(key));
        return item ? item.value : null;
      }
      const pair = specs.find(([specKey]) => normalizeSpecKey(specKey) === normalizeSpecKey(key));
      return pair ? pair[1] : null;
    }
    if (specs[key] !== undefined) return specs[key];
    const matchedKey = Object.keys(specs).find(
      (specKey) => normalizeSpecKey(specKey) === normalizeSpecKey(key)
    );
    return matchedKey ? specs[matchedKey] : null;
  };

  const toValueArray = (value) => {
    if (value == null) return null;
    return Array.isArray(value) ? value : [value];
  };

  const buildSpecsFromProducts = (products) => {
    const specKeyMap = new Map();
    products.forEach((p) => {
      getSpecKeysFromProduct(p.specs).forEach((key) => {
        const normalized = normalizeSpecKey(key);
        if (!specKeyMap.has(normalized)) {
          specKeyMap.set(normalized, {
            key,
            label: isLabelValueSpecs(p.specs)
              ? p.specs.find((s) => normalizeSpecKey(s.label) === normalized)?.label || formatSpecLabel(key)
              : formatSpecLabel(key),
          });
        }
      });
    });
    return Array.from(specKeyMap.values());
  };

  const specHasVisibleValue = (products, key) =>
    products.some((p) => !isEmptySpecValue(getSpecValues(p, key)));

  const getComparableSpecText = (product, key) => {
    const values = toValueArray(getSpecValues(product, key));
    if (!values || isEmptySpecValue(values)) return '';
    return values
      .map((v) => normalizeSpecText(typeof v === 'string' ? v : extractRichText(v)))
      .filter(Boolean)
      .join('|');
  };

  const getSpecDisplayHtml = (value) => {
    if (typeof value === 'string') return value;
    return extractRichText(value);
  };

  const resetCompareHeights = (root) => {
    if (!root) return;
    root
      .querySelectorAll(
        '.product-img-wrapper, .compare-modal-column__media, .product-info > h3, .compare-modal-column__title, .price-container, .compare-modal-column__price, .spec-list > li, .compare-modal-column__specs > li'
      )
      .forEach((el) => {
        el.style.height = '';
      });
  };

  const alignCompareHeights = (root) => {
    if (!root) return;
    const products = root.querySelectorAll(
      '.product-list > .product:not(.product-placeholder), .compare-modal-column__card:not(.is-empty)'
    );
    if (!products.length) return;

    const alignRows = (selector) => {
      products.forEach((product) => {
        product.querySelectorAll(selector).forEach((el) => {
          el.style.height = '';
        });
      });

      const rowCount = products[0].querySelectorAll(selector).length;
      if (!rowCount) return;

      for (let i = 0; i < rowCount; i++) {
        let maxHeight = 0;
        const rowItems = [];

        products.forEach((product) => {
          const item = product.querySelectorAll(selector)[i];
          if (!item) return;
          const height = item.getBoundingClientRect().height;
          if (height > maxHeight) maxHeight = height;
          rowItems.push(item);
        });

        if (maxHeight > 0) {
          rowItems.forEach((item) => {
            item.style.height = `${maxHeight}px`;
          });
        }
      }
    };

    alignRows('.product-img-wrapper, .compare-modal-column__media');
    alignRows('.product-info > h3, .compare-modal-column__title');
    alignRows('.price-container, .compare-modal-column__price');
    alignRows('.spec-list > li, .compare-modal-column__specs > li');
  };

  let alignFrameId = 0;
  const scheduleCompareAlign = (root) => {
    cancelAnimationFrame(alignFrameId);
    alignFrameId = requestAnimationFrame(() => {
      alignFrameId = requestAnimationFrame(() => {
        alignCompareHeights(root);
      });
    });
  };

  const normalizeProductId = (id) => String(id);

  const getDefaultProductIds = (products, limit) =>
    (products || []).slice(0, limit).map((product) => product.id);

  const debounce = (fn, delay) => {
    let timer = null;
    const debounced = function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn.apply(this, args);
      }, delay);
    };
    debounced.cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    return debounced;
  };

  document.addEventListener('alpine:init', () => {
    Alpine.data('compareApp', (config) => ({
      products: config.products || [],
      maxProducts: config.maxProducts || 3,
      shopNowLabel: config.shopNowLabel || 'Shop now',
      limitModalMessage: config.limitModalMessage || 'You can compare up to 3 products at a time.',
      placeholderLabel:
        config.placeholderLabel || 'Select a product from the sidebar to compare.',
      sectionId: config.sectionId,
      selectedIds: [],
      showLimitModal: false,
      productDrawerOpen: false,
      _alignDebounced: null,

      init() {
        this.selectedIds = getDefaultProductIds(this.products, this.maxProducts);

        const root = document.getElementById(`shopify-section-${this.sectionId}`);
        this._alignDebounced = debounce(() => scheduleCompareAlign(root), 150);

        this.$watch('layoutKey', () => {
          resetCompareHeights(root);
          scheduleCompareAlign(root);
          setTimeout(() => scheduleCompareAlign(root), 150);
          setTimeout(() => scheduleCompareAlign(root), 450);
        });

        root?.addEventListener(
          'load',
          (event) => {
            if (event.target.matches?.('.product-img, .compare-modal-column__image')) this._alignDebounced();
          },
          true
        );

        window.addEventListener('resize', this._alignDebounced);
      },

      get selectedProducts() {
        const selected = new Set(this.selectedIds.map(normalizeProductId));
        return this.products.filter((p) => selected.has(normalizeProductId(p.id)));
      },

      get compareSlots() {
        const slots = this.selectedProducts.map((product) => ({
          type: 'product',
          product,
        }));

        while (slots.length < this.maxProducts) {
          slots.push({ type: 'placeholder' });
        }

        return slots;
      },

      get availableSpecs() {
        return buildSpecsFromProducts(this.selectedProducts).filter((s) =>
          specHasVisibleValue(this.selectedProducts, s.key)
        );
      },

      get visibleSpecs() {
        return this.availableSpecs;
      },

      get layoutKey() {
        return `${this.selectedIds.join('|')}|${this.visibleSpecs.length}`;
      },

      get productCountClass() {
        const count = this.selectedProducts.length;
        if (count <= 1) return 'is-count-1';
        if (count === 2) return 'is-count-2';
        return 'is-count-3';
      },

      isSelected(id) {
        const normalizedId = normalizeProductId(id);
        return this.selectedIds.some((selectedId) => normalizeProductId(selectedId) === normalizedId);
      },

      toggleProduct(id) {
        const normalizedId = normalizeProductId(id);
        if (this.isSelected(id)) {
          this.selectedIds = this.selectedIds.filter(
            (pid) => normalizeProductId(pid) !== normalizedId
          );
          return;
        }
        if (this.selectedIds.length >= this.maxProducts) {
          this.showLimitModal = true;
          return;
        }
        this.selectedIds = [...this.selectedIds, id];
      },

      removeProduct(id) {
        const normalizedId = normalizeProductId(id);
        this.selectedIds = this.selectedIds.filter(
          (pid) => normalizeProductId(pid) !== normalizedId
        );
      },

      closeLimitModal() {
        this.showLimitModal = false;
      },

      getSpecValues(product, key) {
        return getSpecValues(product, key);
      },

      toValueArray(value) {
        return toValueArray(value);
      },

      isEmptySpecValue(value) {
        return isEmptySpecValue(value);
      },

      getSpecDisplayHtml(value) {
        return getSpecDisplayHtml(value);
      },

      hasComparePrice(product) {
        return product.original_compare_price > product.original_price;
      },
    }));

    Alpine.data('compareModalApp', (config) => ({
      products: config.products || [],
      shopNowLabel: config.shopNowLabel || 'Shop now',
      selectedLabel: config.selectedLabel || '(selected)',
      placeholderLabel: config.placeholderLabel || 'Select a product',
      maxSlots: config.maxSlots || 3,
      minSlots: config.minSlots || 2,
      modalId: config.modalId,
      slotProductIds: [],
      isOpen: false,
      _alignDebounced: null,

      init() {
        const root = document.getElementById(this.modalId);
        this._alignDebounced = debounce(() => scheduleCompareAlign(root), 150);

        this.$watch('layoutKey', () => {
          resetCompareHeights(root);
          scheduleCompareAlign(root);
          setTimeout(() => scheduleCompareAlign(root), 150);
          setTimeout(() => scheduleCompareAlign(root), 450);
        });

        root?.addEventListener(
          'load',
          (event) => {
            if (event.target.matches?.('.product-img, .compare-modal-column__image')) this._alignDebounced();
          },
          true
        );

        window.addEventListener('resize', this._alignDebounced);
        window.addEventListener('collection-compare:open', () => this.openFromStorage());
      },

      getProductById(id) {
        return this.products.find(
          (product) => normalizeProductId(product.id) === normalizeProductId(id)
        );
      },

      padSlotIds(ids) {
        const result = ids.map((id) => Number(id));
        while (result.length < this.maxSlots) {
          result.push(null);
        }
        return result.slice(0, this.maxSlots);
      },

      isSlotEmpty(slotIndex) {
        const id = this.slotProductIds[slotIndex];
        return id == null || id === '';
      },

      openFromStorage() {
        if (!Array.isArray(window.comparedProducts) || window.comparedProducts.length < this.minSlots) {
          return;
        }

        const ids = window.comparedProducts
          .map((item) => Number(item.id))
          .filter((id) =>
            this.products.some((product) => normalizeProductId(product.id) === normalizeProductId(id))
          )
          .slice(0, this.maxSlots);

        if (ids.length < this.minSlots) return;

        this.slotProductIds = this.padSlotIds(ids);
        this.isOpen = true;
        document.documentElement.classList.add('compare-modal-open');
      },

      close() {
        this.isOpen = false;
        document.documentElement.classList.remove('compare-modal-open');
      },

      get selectedProducts() {
        return this.slotProductIds.map((id) => this.getProductById(id)).filter(Boolean);
      },

      getSelectOptions(slotIndex) {
        const isEmpty = this.isSlotEmpty(slotIndex);
        const options = [];

        if (isEmpty) {
          options.push({
            id: '',
            label: this.placeholderLabel,
            disabled: false,
          });
        }

        this.products.forEach((product) => {
          const isSelectedElsewhere = this.slotProductIds.some(
            (slotId, index) =>
              index !== slotIndex &&
              slotId != null &&
              normalizeProductId(slotId) === normalizeProductId(product.id)
          );

          if (isSelectedElsewhere) return;

          options.push({
            id: product.id,
            label: product.name,
            disabled: false,
          });
        });

        return options;
      },

      changeSlotProduct(slotIndex, newProductId) {
        if (newProductId == null || newProductId === '') {
          const nextIds = [...this.slotProductIds];
          nextIds[slotIndex] = null;
          this.slotProductIds = nextIds;
          return;
        }

        const normalizedNewId = normalizeProductId(newProductId);
        const currentSlotId = this.slotProductIds[slotIndex];

        if (currentSlotId != null && normalizedNewId === normalizeProductId(currentSlotId)) return;

        const isAlreadySelected = this.slotProductIds.some(
          (slotId, index) =>
            slotId != null &&
            index !== slotIndex &&
            normalizeProductId(slotId) === normalizedNewId
        );

        if (isAlreadySelected) return;

        const nextIds = [...this.slotProductIds];
        nextIds[slotIndex] = Number(newProductId);
        this.slotProductIds = nextIds;
      },

      isSlotOptionSelected(slotIndex, optionId) {
        const slotId = this.slotProductIds[slotIndex];
        if (optionId === '' || optionId == null) {
          return slotId == null || slotId === '';
        }
        return slotId != null && normalizeProductId(slotId) === normalizeProductId(optionId);
      },

      get availableSpecs() {
        return buildSpecsFromProducts(this.selectedProducts).filter((s) =>
          specHasVisibleValue(this.selectedProducts, s.key)
        );
      },

      get visibleSpecs() {
        return this.availableSpecs;
      },

      get layoutKey() {
        return `${this.slotProductIds.join('|')}|${this.visibleSpecs.length}`;
      },

      get productCountClass() {
        return 'is-count-3';
      },

      getSpecValues(product, key) {
        return getSpecValues(product, key);
      },

      toValueArray(value) {
        return toValueArray(value);
      },

      isEmptySpecValue(value) {
        return isEmptySpecValue(value);
      },

      getSpecDisplayHtml(value) {
        return getSpecDisplayHtml(value);
      },

      hasComparePrice(product) {
        return product.original_compare_price > product.original_price;
      },
    }));
  });
})();
